define((require) => {
    "use strict";

    const TGroup             = require("ui/experience/widgets/transformations/TGroup");
    const {pointInsideRect}  = require("shared/util/MathUtil").default;
    const {find}             = require("underscore");

    /**
     * Control class used as a control point in transformation
     * the transformation handlers are made up several controls, @see{Controls} class
     *
     * @param type
     * @param u
     * @param v
     * @param offsetX
     * @param offsetY
     * @param cursor
     * @constructor
     */
    function Control(type, u, v, offsetX, offsetY, cursor) {
        this.element = null;
        this.type = type;

        this.x = 0;
        this.y = 0;
        this.width  = 0;
        this.height = 0;
        this.matrix = null;

        this.offsetX = offsetX || 0;
        this.offsetY = offsetY || 0;

        this.size = 8;
        this.shape = null;

        this.cursor = cursor;

        this.u = u;
        this.v = v;

        this.id = Control.idPrefix + (++Control.idCounter);
        this.setDefaultShape();
    }

    Control.SCALE          = "scale";
    Control.SCALE_X        = "scaleX";
    Control.SCALE_Y        = "scaleY";
    Control.ROTATE         = "rotate";
    Control.SKEW_X         = "skewX";
    Control.SKEW_Y         = "skewY";
    Control.BORDER         = "border";
    Control.TARGET         = "target";
    Control.REGISTRATION   = "registration";
    Control.SCALE_UNIFORM  = "scaleUniform";
    Control.ROTATE_SCALE   = "rotateScale";
    Control.EDGE_TRANSLATE = "edgeTranslate"; // Enables translation outside of the target area

    Control.SHAPE_CIRCLE = "circle";
    Control.SHAPE_SQUARE = "square";
    Control.SHAPE_BORDER = "border";
    Control.SHAPE_NONE   = "none";

    Control.idCounter = 0;
    Control.idPrefix  = "tran-control-";

    Control.prototype = {
        setDefaultShape() {
            switch (this.type) {
                case Control.ROTATE:
                case Control.ROTATE_SCALE:
                    this.shape = Control.SHAPE_CIRCLE;
                    break;

                case Control.SCALE:
                case Control.SCALE_UNIFORM:
                case Control.SCALE_X:
                case Control.SCALE_Y:
                case Control.SKEW_X:
                case Control.SKEW_Y:
                    this.shape = Control.SHAPE_SQUARE;
                    break;

                case Control.EDGE_TRANSLATE:
                    this.shape = Control.SHAPE_NONE;
                    break;

                case Control.BORDER:
                    this.shape = Control.SHAPE_BORDER;
                    break;

                default:
                    break;
            }
        },

        /**
         * Update `this` control's position.
         *
         * @param {Matrix} matrix - The matrix of the target.
         * @param {Number} width - The width of the target.
         * @param {Number} height - The height of the target.
         */
        updatePosition(matrix, width, height) {
            const {x, y} = this.computePositionFromTargetMatrix(matrix, width, height);

            this.x = x;
            this.y = y;

            switch (this.shape) {
                case Control.SHAPE_CIRCLE:
                case Control.SHAPE_SQUARE: {
                    this.element.style.left = this.x - this.size / 2 + "px";
                    this.element.style.top = this.y - this.size / 2 + "px";
                    this.element.style.transform = `rotate(${matrix.getRotation()}deg)`;
                    break;
                }

                case Control.SHAPE_BORDER: {
                    const appliedMatrix = matrix.clone();
                    const leftTop = appliedMatrix.getGlobalCoordByLocalCoord(-1, -1);

                    appliedMatrix.x = leftTop.x;
                    appliedMatrix.y = leftTop.y;
                    this.width = width + 2;
                    this.height = height + 2;

                    this.element.style.width = `${this.width}px`;
                    this.element.style.height = `${this.height}px`;
                    this.element.style.transform = appliedMatrix.toString();
                    break;
                }

                case Control.SHAPE_NONE: {
                    this.width = width + Math.abs(this.offsetX * 2);
                    this.height = height + Math.abs(this.offsetY * 2);
                    this.element.style.left = `${this.x}px`;
                    this.element.style.top = `${this.y}px`;
                    this.element.style.width = `${this.width}px`;
                    this.element.style.height = `${this.height}px`;
                    this.element.style.transform = `rotate(${matrix.getRotation()}deg)`;
                    break;
                }

                default:
                    break;
            }

            this.updateCursor(matrix);
        },

        setDisplay(value) {
            if (this.element) {
                this.element.style.display = value;
            }
        },

        setClass(value) {
            if (this.element) {
                this.element.classList.add(value);
            }
        },

        removeClass(value) {
            if (this.element) {
                this.element.classList.remove(value);
            }
        },

        /**
         * Update cursor of this control when rotating
         * @param matrix
         */
        updateCursor(matrix) {
            const tempRotate  = matrix.getRotation();
            const rotate      = tempRotate < 0 ? tempRotate + 360 : tempRotate;
            const indicators  = ["nw", "n", "ne", "e", "se", "s", "sw", "w"]; // n is abbreviation of north, ne = north east, etc.
            const ranges      = [22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5];

            let index = 0;
            let n;
            let i;

            for (i = 0; i < 8; i++) {
                if (rotate < ranges[i]) {
                    index = i;
                    break;
                }
            }

            n = indicators.indexOf(this.cursor);

            if (n !== -1) {
                n = index + n;
                n = n > 7 ? n - 8 : n;
                this.element.style.cursor = `${indicators[n]}-resize`;
            }
        },

        draw(container) {
            if (container.getElementsByClassName(this.id)[0]) {
                // no need to draw again
                return;
            }

            if (this.type === Control.ROTATE_SCALE) {
                this.size = 10;
            }

            if (this.shape) {
                this.element = document.createElement("div");
                this.element.className = `${this.id} ${this.shape} ${this.toDashedName(this.type)}`;
                this.element.style.width = this.size + "px";
                this.element.style.height = this.size + "px";
                this.element.style.display = "none";
                container.appendChild(this.element);
            }
        },

        undraw() {
            const elem = this.element;
            if (elem && elem.parentNode) {
                elem.parentNode.removeChild(elem);
            }
        },

        toDashedName(name) {
            return name.replace(/([A-Z])/g, ($1) => `-${$1.toLowerCase()}`);
        },

        insideEdge(collisionPoint, edgeRect, angle) {
            const innerRect = {
                width: edgeRect.width + (this.offsetX * 2),
                height: edgeRect.height + (this.offsetY * 2),
                x: edgeRect.x - this.offsetX,
                y: edgeRect.y - this.offsetY
            };

            const collidesWithEdgeRect = pointInsideRect(edgeRect, collisionPoint, angle);
            const collidesWithInnerRect = pointInsideRect(innerRect, collisionPoint, angle, {x: edgeRect.x, y: edgeRect.y});

            return collidesWithEdgeRect && !collidesWithInnerRect;
        },

        computePositionFromTargetMatrix(matrix, width, height) {
            const w = width * this.u;
            const h = height * this.v;

            let x = matrix.x + matrix.a * w + matrix.c * h;
            let y = matrix.y + matrix.d * h + matrix.b * w;

            // offset
            let angle;

            if (this.offsetX) {
                angle = matrix.getRotationX();
                x += this.offsetX * Math.cos(angle);
                y += this.offsetX * Math.sin(angle);
            }
            if (this.offsetY) {
                angle = matrix.getRotationY();
                x += this.offsetY * Math.sin(angle);
                y += this.offsetY * Math.cos(angle);
            }

            return {x, y};
        },

        /**
         * @typedef {Object} TranslateBounds
         * @property {Number} x - The x coordinate.
         * @property {Number} y - The y coordinate.
         * @property {Number} width - The width.
         * @property {Number} height - The height.
         * @property {Number} angle - The angle, in degrees.
         */

        /**
         * From the current target, populates an array of rectangular bounds, representing the current selected widgets bounds.
         *
         * @param {TElement|TGroup} target The current target.
         * @return {TranslateBounds[]} Array of bounds.
         */
        parseEdgeTranslateTarget(target) {
            const getAngle = (element) => element.getMatrix().getRotation() * -1;

            if (target instanceof TGroup) {
                return target.members.map((element) => {
                    const {x, y} = this.computePositionFromTargetMatrix(element.getMatrix(), this.width, this.height);

                    return {
                        width: element.width - (this.offsetX * 2),
                        height: element.height - (this.offsetY * 2),
                        x,
                        y,
                        angle: getAngle(element)
                    };
                });
            }
            return [{
                width: this.width,
                height: this.height,
                x: this.x,
                y: this.y,
                angle: getAngle(target)
            }];
        },

        /**
         * Check if the point(x, y) is within the range of this control area
         *
         * @param x
         * @param y
         * @returns {boolean} true if yes, false no
         */
        contains(x, y, target) {
            const visible = this.element && this.element.style.display !== "none";

            // Explicitly check for edge collision.
            if (this.type === Control.EDGE_TRANSLATE) {
                const collisionPoint = {x, y};
                const targetBounds = this.parseEdgeTranslateTarget(target);
                const inside = !!find(targetBounds, (rect) => this.insideEdge(collisionPoint, rect, rect.angle));

                return inside && visible;
            }

            const cx = Math.abs(this.x - x);
            const cy = Math.abs(this.y - y);
            const sr = this.size / 2;

            return cx < sr && cy < sr && visible;
        }
    };

    return Control;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/transformations/Control.js